import React, { useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppSelect from "@/features/line-sheet-sets/app-select";
import FilterChip from "@/shared/components/filter/FilterChip";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useGetFavoriteBrands from "@/shared/hooks/favorite-brands/useGetFavoriteBrands";
import useUpdateFavoriteBrands from "@/shared/hooks/favorite-brands/useUpdateFavoriteBrands";
import useGetPathPickFilterInfo from "@/features/pathpick/features/search-products/hooks/query/useGetPathPickFilterInfo";
import { toPathPickBrandOptions } from "@/features/pathpick/utils";
import { MAX_FAVORITE_BRANDS } from "@/features/pathpick/features/search-products/constants";

// 칩 라벨에 브랜드명이 필요해 id만이 아니라 이름까지 들고 있는다.
interface DraftBrand {
  id: number;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

function PathPickFavoriteBrandsModal({ isOpen, onClose }: Props) {
  const { t } = useI18nHelper();
  const { info } = useAppToasts({ id: "GLOBAL" });
  const { favoriteBrands } = useGetFavoriteBrands();
  // 셀렉터 옵션은 상품 리스트 필터와 같은 소스(filter-info)를 쓴다 — 같은 쿼리키라 추가 요청이 없다.
  const { pathPickFilterInfo } = useGetPathPickFilterInfo();
  const { mutate: updateFavoriteBrands, isPending } = useUpdateFavoriteBrands();

  // filter-info 브랜드는 id/name이 nullable이라 옵션으로 쓸 수 있는 것만 남겨서 다룬다.
  const filterBrands = toPathPickBrandOptions(pathPickFilterInfo?.brands);

  const savedBrands: DraftBrand[] = favoriteBrands.brands.map((brand) => ({
    id: brand.id,
    name: brand.name,
  }));

  // 조건부 마운트(열 때마다 fresh mount) 전제라 초기값 동기화 effect가 필요 없다.
  const [draft, setDraft] = useState<DraftBrand[]>(savedBrands);

  const selectedIds = draft.map((brand) => brand.id);
  const isDirty =
    JSON.stringify(selectedIds) !==
    JSON.stringify(savedBrands.map((brand) => brand.id));
  const isFull = draft.length >= MAX_FAVORITE_BRANDS;

  // 이미 담은 브랜드는 옵션에서 빼서 중복 선택 자체를 막는다.
  const options = filterBrands
    .filter((brand) => !selectedIds.includes(brand.id))
    .map((brand) => ({ name: brand.name, value: brand.id }));

  const handleAdd = (brandId?: number) => {
    if (brandId === undefined || isFull) {
      return;
    }
    const brand = filterBrands.find((item) => item.id === brandId);
    if (!brand) {
      return;
    }
    setDraft((prev) => [...prev, { id: brand.id, name: brand.name }]);
  };

  const handleRemove = (brandId: number) => {
    setDraft((prev) => prev.filter((brand) => brand.id !== brandId));
  };

  const handleSave = () => {
    updateFavoriteBrands(
      { brandIds: selectedIds },
      {
        onSuccess: () => {
          info(
            t("path_pick:search_products.popular.favorite_brands_save_success")
          );
          onClose();
        },
      }
    );
  };

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // minWidth가 maxWidth를 이기고, 가운데 정렬된 모달은 왼쪽으로 넘치면
      // 스크롤로도 닿을 수 없어 화면 폭으로 클램프한다.
      minWidth={"min(480px, 92dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t(
          "path_pick:search_products.popular.favorite_brands_modal_title"
        )}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"12px"}>
          <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
            {t(
              "path_pick:search_products.popular.favorite_brands_modal_description",
              { count: MAX_FAVORITE_BRANDS }
            )}
          </Text>
          <Flex flexDirection={"column"} gap={"4px"}>
            <Text textStyle={"body-sm-bold"} color={"#444440"}>
              {t("path_pick:filter.brand")}
            </Text>
            <AppSelect<number>
              name={t("path_pick:search_products.popular.favorite_brands_add")}
              options={options}
              value={undefined}
              onSelect={handleAdd}
              isDisabled={isFull}
              isClearable={false}
              width={"100%"}
              dropdownWidth={"100%"}
              // 드롭다운은 Portal을 쓰지 않아 모달의 overflow: hidden에 잘린다.
              // position: fixed면 컨테이닝 블록이 viewport라 조상 overflow의 적용 대상이 아니게 된다.
              popoverStrategy={"fixed"}
            />
          </Flex>
          <Flex
            padding={"12px"}
            borderRadius={"8px"}
            border={"0.5px solid #D4D4D1"}
            gap={"8px"}
            flexWrap={"wrap"}
          >
            {draft.length > 0 ? (
              draft.map((brand) => (
                <FilterChip
                  key={brand.id}
                  label={brand.name}
                  colorVariant={"gray"}
                  onRemove={() => handleRemove(brand.id)}
                />
              ))
            ) : (
              <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
                {t(
                  "path_pick:search_products.popular.favorite_brands_empty_selection"
                )}
              </Text>
            )}
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        leftContent={
          <Flex
            as={"button"}
            type={"button"}
            alignItems={"center"}
            gap={"4px"}
            color={"#8F8F8C"}
            cursor={"pointer"}
            onClick={() => setDraft(savedBrands)}
            _hover={{ color: "#444440" }}
          >
            <CustomIcon src={"arrow-rotate"} width={16} height={16} />
            <Text textStyle={"body-sm-medium"}>
              {t("path_pick:search_products.popular.favorite_brands_reset")}
            </Text>
          </Flex>
        }
        cancelName={t("common:cancel")}
        confirmName={t("common:save")}
        onCancel={onClose}
        onConfirm={handleSave}
        isLoading={isPending}
        isConfirmDisabled={!isDirty}
      />
    </CustomApprovalModal>
  );
}

export default PathPickFavoriteBrandsModal;
